/**
 * EMP-ID-CHANGE-001 — two-step Employee ID change dialog (Admin / HR Manager).
 * Step 1: new ID (live duplicate check) + mandatory reason + warning.
 * Step 2: confirmation (name, current ID, new ID, reason) before saving.
 */
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, Pencil } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../../ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL || '';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export const EmployeeIdChangeDialog = ({ open, employee, onClose, onChanged }) => {
  const [step, setStep] = useState(1);
  const [newId, setNewId] = useState('');
  const [reason, setReason] = useState('');
  const [dupError, setDupError] = useState(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState(null);
  const dupTimer = useRef(null);
  const idemKey = useRef(null);

  useEffect(() => {
    if (open) {
      setStep(1); setNewId(''); setReason(''); setDupError(null); setApiError(null);
      idemKey.current = `eidchg-${employee?.employee_id}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    }
  }, [open, employee?.employee_id]);

  const onNewIdChange = (v) => {
    setNewId(v);
    setDupError(null); setApiError(null);
    clearTimeout(dupTimer.current);
    const trimmed = v.trim();
    if (!trimmed || trimmed === employee.employee_id) return;
    dupTimer.current = setTimeout(async () => {
      setChecking(true);
      try {
        const params = new URLSearchParams({ employee_id: trimmed, exclude_employee_id: employee.employee_id });
        const res = await axios.get(`${API}/api/hr/employees/check-duplicate?${params}`, { headers: authHeaders() });
        if (res.data.employee_id_taken) {
          setDupError(`This Employee ID is already in use or was previously used (${res.data.employee_id_owner}).`);
        }
      } catch { /* non-blocking; backend re-validates on save */ }
      finally { setChecking(false); }
    }, 400);
  };

  const trimmedId = newId.trim();
  const sameAsCurrent = trimmedId === employee?.employee_id;
  const step1Valid = trimmedId.length > 0 && !sameAsCurrent && reason.trim().length >= 5 && !dupError && !checking;

  const save = async () => {
    setSaving(true); setApiError(null);
    try {
      const res = await axios.post(`${API}/api/hr/employees/${employee.employee_id}/change-id`, {
        new_employee_id: trimmedId,
        reason: reason.trim(),
        idempotency_key: idemKey.current,
      }, { headers: authHeaders() });
      toast.success(`Employee ID changed: ${res.data.previous_employee_id} → ${res.data.new_employee_id}`, {
        description: `${res.data.employee_name} — profile refreshed`,
      });
      onChanged(res.data.new_employee_id);
    } catch (e) {
      setApiError(e.response?.data?.detail || 'Failed to change Employee ID');
      setStep(1);
    } finally { setSaving(false); }
  };

  if (!employee) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="employee-id-change-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="w-4 h-4 text-blue-600" />
            {step === 1 ? 'Edit Employee ID' : `Change Employee ID for ${employee.full_name}?`}
          </DialogTitle>
          <DialogDescription className="sr-only">Change the business Employee ID with a mandatory reason and confirmation</DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Current Employee ID</label>
              <Input value={employee.employee_id} disabled className="h-9 bg-slate-50" data-testid="eid-current-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1" htmlFor="eid-new">
                New Employee ID <span className="text-rose-500">*</span>
              </label>
              <Input id="eid-new" value={newId} onChange={(e) => onNewIdChange(e.target.value)}
                placeholder="e.g. EMP-102" className={`h-9 ${dupError ? 'border-rose-300' : ''}`}
                data-testid="eid-new-input" />
              {checking && <p className="text-[11px] text-slate-400 mt-1">Checking availability…</p>}
              {sameAsCurrent && trimmedId && <p className="text-[11px] text-rose-600 mt-1" data-testid="eid-same-error">The new ID must be different from the current ID.</p>}
              {dupError && <p className="text-[11px] text-rose-600 mt-1" data-testid="eid-dup-error">{dupError}</p>}
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1" htmlFor="eid-reason">
                Reason for change <span className="text-rose-500">*</span>
              </label>
              <Textarea id="eid-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Incorrect ID entered during employee creation (min 5 characters)"
                rows={2} maxLength={500} className="text-sm resize-none" data-testid="eid-reason-input" />
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-testid="eid-warning">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-700">
                Changing the Employee ID may affect reports or external references that use the old ID.
                Internal records (leave, attendance, documents, user link) are preserved automatically.
              </p>
            </div>
            {apiError && <p className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="eid-api-error">{apiError}</p>}
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="bg-slate-50 rounded-lg p-3 space-y-1.5 text-slate-700" data-testid="eid-confirm-summary">
              <p><span className="text-slate-400">Employee:</span> <strong>{employee.full_name}</strong></p>
              <p><span className="text-slate-400">Current ID:</span> <strong>{employee.employee_id}</strong></p>
              <p><span className="text-slate-400">New ID:</span> <strong className="text-blue-700">{trimmedId}</strong></p>
              <p><span className="text-slate-400">Reason:</span> {reason.trim()}</p>
            </div>
            <p className="text-xs text-slate-500">This change is audited and takes effect immediately after saving.</p>
          </div>
        )}

        <DialogFooter>
          {step === 1 ? (
            <>
              <Button variant="outline" onClick={onClose} disabled={saving} data-testid="eid-cancel-btn">Cancel</Button>
              <Button onClick={() => setStep(2)} disabled={!step1Valid} data-testid="eid-continue-btn">Continue</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep(1)} disabled={saving} data-testid="eid-back-btn">Back</Button>
              <Button onClick={save} disabled={saving} className="gap-1.5" data-testid="eid-confirm-btn">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Confirm Change
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeIdChangeDialog;
